import { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { BsPlusLg } from "react-icons/bs";
import { FaEdit, FaTrash } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { LuSettings2 } from "react-icons/lu";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CreateCategoryModal from "./CreateCategoryModal";
import "./NotificationCategories.css";
// import 'react-toastify/dist/ReactToastify.css';
import Handshake from "../../../src/assets/svg/handshake 1.png";

const NotificationCategories = () => {
  const [showFilter, setShowFilter] = useState(false);
  const filterRef = useRef();
  const [showModal, setShowModal] = useState(false);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [deletingId, setDeletingId] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  const { user } = useSelector((state) => state.auth);

  const BEARER_TOKEN = user?.token;

  const API_URL =
    "https://estbn.com/EstbnNew/public/api/portal/notification-categories";

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${BEARER_TOKEN}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get("Content-Type");
        let errorData = {};
        if (contentType && contentType.includes("application/json")) {
          errorData = await response.json();
        } else {
          const textResponse = await response.text();
          console.error(
            "Non-JSON error response on initial fetch:",
            textResponse
          );
          throw new Error(
            `Server returned non-JSON error (Status: ${
              response.status
            }). Response: ${textResponse.substring(0, 100)}...`
          );
        }
        throw new Error(
          errorData.message ||
            `HTTP error! status: ${response.status}, message: ${response.statusText}`
        );
      }

      const result = await response.json();
      if (result.success && result.data && result.data.categories) {
        setCategories(result.data.categories);
      } else {
        throw new Error("API response malformed or indicates no success.");
      }
    } catch (e) {
      console.error("Failed to fetch categories:", e);
      setError(e.message);
      toast.error(`Error loading categories: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [API_URL, BEARER_TOKEN]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleCategoryOperationSuccess = (message) => {
    setShowModal(false);
    setEditingCategory(null);
    fetchCategories();
    toast.success(message);
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    setDeletingId(categoryId);

    try {
      const response = await fetch(`${API_URL}/${categoryId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${BEARER_TOKEN}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get("Content-Type");
        let errorData = {};
        if (contentType && contentType.includes("application/json")) {
          errorData = await response.json();
        } else {
          const textResponse = await response.text();
          console.error("Non-JSON error response on delete:", textResponse);
          throw new Error(
            `Server returned non-JSON error (Status: ${
              response.status
            }). Response: ${textResponse.substring(0, 100)}...`
          );
        }
        throw new Error(
          errorData.message ||
            `Failed to delete category: ${response.statusText}`
        );
      }

      await fetchCategories();
      toast.success(`Category "${categoryName}" deleted successfully!`);
    } catch (err) {
      console.error("Error deleting category:", err);
      toast.error(
        err.message || "An unexpected error occurred during deletion."
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="container py-4 notification-container text-center">
        <Spinner animation="border" role="status" className="me-2" />
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <>
      {/* <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover /> */}
      <div className="m-4 py-4 notification-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="fw-semibold">Notification Categories</h5>
          <button
            className="btn border rounded d-flex align-items-center px-3"
            onClick={() => {
              setEditingCategory(null);
              setShowModal(true);
            }}
          >
            <BsPlusLg className="me-2" />
            Add New Category
          </button>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <select className="form-select me-2" style={{ width: "80px" }}>
              <option>10</option>
              <option>20</option>
            </select>
            <span>entries per page</span>
          </div>

          <div className="d-flex align-items-center">
            <div className="position-relative me-2 search-input-wrapper">
              <input
                type="text"
                className="form-control"
                placeholder="Search categories"
              />
              <FiSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
            </div>

            <div className="position-relative" ref={filterRef}>
              <button
                className="btn border rounded"
                onClick={() => setShowFilter((prev) => !prev)}
              >
                <LuSettings2 />
              </button>

              {showFilter && (
                <div className="filter-popup shadow-sm border rounded p-3">
                  <div className="mb-3">
                    <p className="filter-title">Choose Status</p>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="status"
                        id="active"
                      />
                      <label className="form-check-label" htmlFor="active">
                        Active <span>(15)</span>
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="status"
                        id="inactive"
                      />
                      <label className="form-check-label" htmlFor="inactive">
                        Inactive <span>(5)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <p className="filter-title">Choose Type</p>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="type"
                        id="required"
                      />
                      <label className="form-check-label" htmlFor="required">
                        Required <span>(15)</span>
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="type"
                        id="optional"
                      />
                      <label className="form-check-label" htmlFor="optional">
                        Optional <span>(5)</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {categories.length > 0 ? (
            categories.map((cat) => (
              <div className="col-md-4 mb-4" key={cat.id}>
                <div className="card h-100 shadow-sm p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center">
                      {cat.icon_url ? (
                        <img
                          src={cat.icon_url}
                          alt={cat.name}
                          width={32}
                          height={32}
                          className="me-2"
                        />
                      ) : (
                        <img
                          src={Handshake}
                          alt="Default Icon"
                          width={32}
                          height={32}
                          className="me-2"
                        />
                      )}
                      <div>
                        <h6 className="mb-0 fw-semibold">{cat.name}</h6>
                        <small
                          className={`d-flex align-items-center fw-medium ${
                            cat.is_active ? "text-success" : "text-danger"
                          }`}
                        >
                          <span className="status-dot me-1"></span>
                          {cat.is_active ? "Active" : "Inactive"}
                        </small>
                      </div>
                    </div>
                    <div>
                      <FaEdit
                        className="me-2 text-secondary cursor-pointer"
                        onClick={() => {
                          setEditingCategory(cat);
                          setShowModal(true);
                        }}
                      />
                      {deletingId === cat.id ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="text-danger"
                        />
                      ) : (
                        <FaTrash
                          className="text-danger cursor-pointer"
                          onClick={() => handleDeleteCategory(cat.id, cat.name)}
                          disabled={!!deletingId}
                        />
                      )}
                    </div>
                  </div>
                  <p className="mt-3 text-muted small">{cat.description}</p>
                  <div className="d-flex flex-wrap gap-2">
                    {cat.default_channels &&
                      cat.default_channels.map((channel, cIdx) => (
                        <span
                          className="channel-btn btn-sm px-3 py-1"
                          key={cIdx}
                        >
                          {capitalizeFirstLetter(channel)}
                        </span>
                      ))}
                    {cat.required && (
                      <span
                        className="channel-btn btn-sm px-3 py-1 text-danger"
                        style={{ backgroundColor: "#fef2f2" }}
                      >
                        Required
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <p>No categories found.</p>
              {error && (
                <p className="text-danger small">
                  (Initial load error: {error})
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <CreateCategoryModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setEditingCategory(null);
        }}
        onSuccess={handleCategoryOperationSuccess}
        editingCategory={editingCategory}
      />
    </>
  );
};

export default NotificationCategories;
